import { setzeMarkenLieferant } from '../reportServer';
import type { AdminGateway } from '@forq/admin-data';
import type { AuthGateway, DocumentArchiveGateway, RecordDeletionGateway } from '@forq/contracts';
import type { ImportGateway } from '@forq/import-data';
import type { RentalGateway } from '@forq/rental-data';
import type { ExportLogGateway, FinanceGateway, IncomingInvoiceGateway, PeriodClosingGateway } from '@forq/finance-data';
import type { AttachmentDocumentGateway, BugReportGateway, CompanyProfileGateway, UiLayoutGateway } from '@forq/platform-data';
import type { CustomersGateway } from '@forq/customers-data';
import type { MachinesGateway } from '@forq/machines-data';
import type { MasterdataGateway } from '@forq/masterdata-data';
import type { MastersGateway } from '@forq/masters-data';
import type { OrderGateway } from '@forq/orders-data';
import type { PartsGateway } from '@forq/parts-data';
import type { PlatformDataGateway } from '@forq/platform-data';
import type { SalesGateway } from '@forq/sales-data';
import type { ServiceGateway } from '@forq/service-data';
import type { ServiceBillingGateway } from '@forq/service-billing-data';
import type { WarehouseGateway } from '@forq/warehouse-data';
import {
  SupabaseAdminGateway,
  SupabaseAuthGateway,
  SupabaseCustomersGateway,
  SupabaseCompanyProfileGateway,
  SupabaseUiLayoutGateway,
  SupabaseBugReportGateway,
  SupabaseAttachmentDocumentGateway,
  SupabaseExportLogGateway,
  SupabaseFinanceGateway,
  SupabasePeriodClosingGateway,
  SupabaseIncomingInvoiceGateway,
  SupabaseImportGateway,
  SupabaseMachinesGateway,
  SupabaseMasterdataGateway,
  SupabaseMastersGateway,
  SupabaseOrdersGateway,
  SupabasePartsGateway,
  SupabasePlatformDataGateway,
  SupabaseRecordDeletionGateway,
  SupabaseRentalGateway,
  SupabaseSalesGateway,
  SupabaseServiceGateway,
  SupabaseWarehouseGateway,
  createForqSupabaseClient,
  SupabaseDocumentArchiveGateway,
  SupabaseServiceBillingGateway,
  isSupabaseConfigured,
  type ForqSupabaseConfig,
} from '@forq/supabase';
import { partsOrderingService } from '../partsServer';

export interface SupabaseEnvironment {
  readonly VITE_SUPABASE_URL?: string;
  readonly VITE_SUPABASE_ANON_KEY?: string;
}

export type BrowserBootstrapConfiguration =
  | { readonly kind: 'demo' }
  | { readonly kind: 'configuration-error'; readonly message: string }
  | { readonly kind: 'supabase'; readonly config: ForqSupabaseConfig };

export interface SupabaseRuntimeServices {
  readonly auth: AuthGateway;
  readonly platformData: PlatformDataGateway;
  readonly admin: AdminGateway;
  readonly import: ImportGateway;
  readonly deletion: RecordDeletionGateway;
  readonly rental: RentalGateway;
  readonly finance: FinanceGateway;
  readonly incoming: IncomingInvoiceGateway;
  readonly exportLog: ExportLogGateway;
  readonly periodClosing: PeriodClosingGateway;
  readonly companyProfile: CompanyProfileGateway;
  readonly uiLayout: UiLayoutGateway;
  readonly bugReports: BugReportGateway;
  readonly attachmentDocuments: AttachmentDocumentGateway;
  readonly customers: CustomersGateway;
  readonly machines: MachinesGateway;
  readonly masterdata: MasterdataGateway;
  readonly masters: MastersGateway;
  readonly orders: OrderGateway;
  readonly parts: PartsGateway;
  readonly sales: SalesGateway;
  readonly service: ServiceGateway;
  /** Service-Rechnungsvorschlaege -- bis 09/2026 lief hier auch produktiv der Demo-Gateway. */
  readonly serviceBilling: ServiceBillingGateway;
  /** Belegarchiv (§ 14b UStG): der ausgestellte Beleg, so wie er ausgestellt wurde. */
  readonly documentArchive: DocumentArchiveGateway;
  readonly warehouse: WarehouseGateway;
}

let cachedServices: { readonly key: string; readonly services: SupabaseRuntimeServices } | null = null;

/**
 * Ein relativer Wert bedeutet: dieselbe Herkunft wie diese Seite.
 *
 * Gebraucht wird das vom FORQ Server, der die Anwendung selbst ausliefert. Er
 * kennt beim Bauen seinen späteren Namen nicht — dieselbe Datei läuft unter
 * `http://ftw-server:5280` wie hinter Cloudflare. Ein fest eingebauter
 * absoluter Wert wäre in genau einem der beiden Fälle richtig.
 *
 * Für Cloudflare/Supabase ändert sich nichts: dort steht eine vollständige
 * https-Adresse, und die läuft unverändert durch.
 */
function resolveOrigin(value: string): string {
  if (!value.startsWith('/')) return value;
  if (typeof window === 'undefined') return value;
  return value === '/' ? window.location.origin : `${window.location.origin}${value.replace(/\/+$/, '')}`;
}

export function resolveSupabaseBootstrapConfiguration(
  environment: SupabaseEnvironment,
): BrowserBootstrapConfiguration {
  const url = resolveOrigin(environment.VITE_SUPABASE_URL?.trim() ?? '');
  const anonKey = environment.VITE_SUPABASE_ANON_KEY?.trim() ?? '';

  if (!url && !anonKey) return { kind: 'demo' };
  if (!url || !anonKey) {
    return {
      kind: 'configuration-error',
      message: 'Supabase ist nur teilweise konfiguriert. URL und Anon-Key müssen gemeinsam gesetzt sein.',
    };
  }

  const config = { url, anonKey };
  if (!isSupabaseConfigured(config)) {
    return {
      kind: 'configuration-error',
      message: 'Die Supabase-Konfiguration ist unvollständig.',
    };
  }

  try {
    const parsedUrl = new URL(url);
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) throw new Error('Unsupported protocol.');
  } catch {
    return {
      kind: 'configuration-error',
      message: 'Die Supabase-URL ist ungültig.',
    };
  }

  return { kind: 'supabase', config };
}

export function createSupabaseRuntimeServices(
  config: ForqSupabaseConfig,
): SupabaseRuntimeServices {
  const key = `${config.url.trim()}\u0000${config.anonKey.trim()}`;
  if (cachedServices?.key === key) return cachedServices.services;

  const client = createForqSupabaseClient(config);

  // Der Beleg-Endpunkt des eigenen Servers braucht dieselbe Sitzungsmarke wie
  // jeder RPC-Aufruf. Ohne sie haengt er allein am Cookie -- und ein
  // Secure-Cookie erreicht einen Client-PC ueber HTTP nie. Siehe reportServer.ts.
  setzeMarkenLieferant(async () => {
    const { data } = await client.auth.getSession();
    return data.session?.access_token ?? null;
  });

  const services: SupabaseRuntimeServices = {
    auth: new SupabaseAuthGateway(client),
    platformData: new SupabasePlatformDataGateway(client),
    admin: new SupabaseAdminGateway(client),
    import: new SupabaseImportGateway(client),
    deletion: new SupabaseRecordDeletionGateway(client),
    rental: new SupabaseRentalGateway(client),
    finance: new SupabaseFinanceGateway(client),
    incoming: new SupabaseIncomingInvoiceGateway(client),
    exportLog: new SupabaseExportLogGateway(client),
    periodClosing: new SupabasePeriodClosingGateway(client),
    companyProfile: new SupabaseCompanyProfileGateway(client),
    uiLayout: new SupabaseUiLayoutGateway(client),
    bugReports: new SupabaseBugReportGateway(client),
    attachmentDocuments: new SupabaseAttachmentDocumentGateway(client),
    customers: new SupabaseCustomersGateway(client),
    machines: new SupabaseMachinesGateway(client),
    masterdata: new SupabaseMasterdataGateway(client),
    masters: new SupabaseMastersGateway(client),
    orders: new SupabaseOrdersGateway(client),
    parts: new SupabasePartsGateway(client, partsOrderingService),
    sales: new SupabaseSalesGateway(client),
    service: new SupabaseServiceGateway(client),
    serviceBilling: new SupabaseServiceBillingGateway(client),
    documentArchive: new SupabaseDocumentArchiveGateway(client),
    warehouse: new SupabaseWarehouseGateway(client),
  };
  cachedServices = { key, services };
  return services;
}
